import { cssVar, tokens } from '@cad-arc';
import type { MapLedgerEventView, MapViewportEventView } from '@mark43/cad-api';
import { colorSoundConfigStore } from '../../configurable-colors-sounds/color-sound-config-store';
import { colors } from '../../colors/colors';
import type { Theme } from '../../theme/theme-store';
import {
    collectConfiguredHexes,
    createTintedIconRegistry,
    getConfiguredHex,
    replaceColors,
    svgToDataUrl,
    toStaleSvg,
    withTextLabelColor,
} from './configured-icon-colors';
import { EVENT_HOVER_OUTLINE_SVG } from './event-hover-outline';
import type { TintableIcon, UniqueValueInfo } from './configured-icon-colors';
import type { LabelClassProperties } from '../types';
import {
    EVENT_ICON_SIZE,
    eventIconLabels,
    eventIconRenderer,
    eventUniqueValueInfos,
} from './events';
import { unitIconLabels, unitIconRenderer, unitUniqueValueInfos } from './units';

// Tinting starts from these. The unselected unit icons are the same in both themes.
import policeAvailableOnlineInactive from '../images/unit-icons/police/online/available/police_available_online_inactive.svg?raw';
import policeUnavailableOnlineInactive from '../images/unit-icons/police/online/unavailable/police_unavailable_online_inactive.svg?raw';
import policeUnmannedOnlineInactive from '../images/unit-icons/police/online/unmanned/police_unmanned_online_inactive.svg?raw';
import policeOnFootOnlineInactive from '../images/unit-icons/police/on-foot/unavailable/police_on_foot_unavailable_online_inactive.svg?raw';
import policeOnFootOnlineAvailableInactive from '../images/unit-icons/police/on-foot/available/police_on_foot_available_online_inactive.svg?raw';
import fireAvailableOnlineInactive from '../images/unit-icons/fire/online/available/fire_available_online_inactive.svg?raw';
import fireUnavailableOnlineInactive from '../images/unit-icons/fire/online/unavailable/fire_unavailable_online_inactive.svg?raw';
import fireUnmannedOnlineInactive from '../images/unit-icons/fire/online/unmanned/fire_unmanned_online_inactive.svg?raw';
import emsAvailableOnlineInactive from '../images/unit-icons/ems/online/available/ems_available_online_inactive.svg?raw';
import emsUnavailableOnlineInactive from '../images/unit-icons/ems/online/unavailable/ems_unavailable_online_inactive.svg?raw';
import policeAvailableOfflineInactive from '../images/unit-icons/police/offline/available/police_available_offline_inactive.svg?raw';
import policeUnavailableOfflineInactive from '../images/unit-icons/police/offline/unavailable/police_unavailable_offline_inactive.svg?raw';
import policeUnmannedOfflineInactive from '../images/unit-icons/police/offline/unmanned/police_unmanned_offline_inactive.svg?raw';
import policeOnFootOfflineInactive from '../images/unit-icons/police/offline/on-foot/police_on_foot_offline_inactive.svg?raw';
import fireAvailableOfflineInactive from '../images/unit-icons/fire/offline/available/fire_available_offline_inactive.svg?raw';
import fireUnavailableOfflineInactive from '../images/unit-icons/fire/offline/unavailable/fire_unavailable_offline_inactive.svg?raw';
import fireUnmannedOfflineInactive from '../images/unit-icons/fire/offline/unmanned/fire_unmanned_offline_inactive.svg?raw';
import emsAvailableOfflineInactive from '../images/unit-icons/ems/offline/available/ems_available_offline_inactive.svg?raw';
import emsUnavailableOfflineInactive from '../images/unit-icons/ems/offline/unavailable/ems_unavailable_offline_inactive.svg?raw';
import todayEventAssignedInactiveSvg from '../images/event-icons/assigned/event_assigned_inactive.svg?raw';
import todayEventUnassignedInactiveSvg from '../images/event-icons/unassigned/event_unassigned_inactive.svg?raw';
import redesignedEventAssignedInactiveSvg from '../images/configured/event-icons/assigned/event_assigned_inactive.svg?raw';
import redesignedEventUnassignedInactiveSvg from '../images/configured/event-icons/unassigned/event_unassigned_inactive.svg?raw';
import redesignedEventAssignedSelectedSvg from '../images/configured/event-icons/assigned/event_assigned_selected.svg?raw';
import redesignedEventUnassignedSelectedSvg from '../images/configured/event-icons/unassigned/event_unassigned_selected.svg?raw';

// The redesign only changes the selected unit icons and all four event icons. Selected unit icons
// don't show the unit's status, so one icon per discipline covers every status.
import redesignedPoliceOnlineSelectedSvg from '../images/configured/unit-icons/police/online/available/police_available_online_selected.svg?raw';
import redesignedPoliceOfflineSelectedSvg from '../images/configured/unit-icons/police/offline/available/police_available_offline_selected.svg?raw';
import redesignedPoliceOnFootOnlineSelectedSvg from '../images/configured/unit-icons/police/on-foot/available/police_on_foot_available_online_selected.svg?raw';
import redesignedPoliceOnFootOfflineSelectedSvg from '../images/configured/unit-icons/police/offline/on-foot/police_on_foot_offline_selected.svg?raw';
import redesignedFireOnlineSelectedSvg from '../images/configured/unit-icons/fire/online/available/fire_available_online_selected.svg?raw';
import redesignedFireOfflineSelectedSvg from '../images/configured/unit-icons/fire/offline/available/fire_available_offline_selected.svg?raw';
import redesignedEmsOnlineSelectedSvg from '../images/configured/unit-icons/ems/online/available/ems_available_online_selected.svg?raw';
import redesignedEmsOfflineSelectedSvg from '../images/configured/unit-icons/ems/offline/available/ems_available_offline_selected.svg?raw';
import redesignedEventAssignedInactive from '../images/configured/event-icons/assigned/event_assigned_inactive.svg';
import redesignedEventUnassignedInactive from '../images/configured/event-icons/unassigned/event_unassigned_inactive.svg';

type UnitIconRenderer = typeof unitIconRenderer;
type EventIconRenderer = typeof eventIconRenderer;
type TintedIconRegistry = ReturnType<typeof createTintedIconRegistry>;

export type EventHoverSymbol = __esri.PictureMarkerSymbolProperties & { type: 'picture-marker' };

/**
 * The map's unit and event icons: today's, or the redesign that ships with configurable colors and
 * sounds. Configured unit status and priority colors are tinted on top of either one.
 */
export interface MapIconTheme {
    eventIconSize: string;
    unitIconLabels: LabelClassProperties[];
    eventIconLabels: LabelClassProperties[];
    /**
     * Whether the selected event is drawn again on its own layer above every other layer. Esri
     * places the labels of higher layers first, so then nothing can hide the selected event's label.
     */
    raisesSelectedEvent: boolean;
    /**
     * Drawn over a hovered event in place of Esri's highlight. Null keeps Esri's highlight, which
     * follows the edge of everything the icon paints.
     */
    eventHoverSymbol: EventHoverSymbol | null;
    /** Includes a tinted variant of every tintable icon in every configured color. */
    getUnitIconRenderer(): UnitIconRenderer;
    getEventIconRenderer(): EventIconRenderer;
    /** The tinted icon name when the unit state has a configured color, else `baseIconName`. */
    getUnitIconName(baseIconName: string, unitStateId: number | null | undefined): string;
    getEventIconName(
        baseIconName: string,
        priorityAttributeId: number | null | undefined,
        agencyTypeGlobalAttrId: number | null | undefined
    ): string;
    /** Image URL for an icon name, including tinted names. Empty when unknown. */
    getUnitIconUrl(iconName: string | undefined): string;
    getEventIconUrl(iconName: string): string;
}

interface MapIconThemeAssets {
    unitIconRenderer: UnitIconRenderer;
    eventIconRenderer: EventIconRenderer;
    eventIconSize: string;
    unitIconLabels: LabelClassProperties[];
    eventIconLabels: LabelClassProperties[];
    raisesSelectedEvent: boolean;
    eventHoverSymbol: EventHoverSymbol | null;
    tintableUnitIcons: TintedIconRegistry;
    tintableEventIcons: TintedIconRegistry;
}

// Unselected icons carry the status color. Selected icons are a fixed selection color, and the
// EN_ROUTE_ALTERNATE icons are animated GIFs, so neither can be recolored.
const onlineUnitIcons = {
    'Police, Available, Online, Inactive': {
        svg: policeAvailableOnlineInactive,
        accentColors: ['#8EC5FF'],
    },
    'Police, Unavailable, Online, Inactive': {
        svg: policeUnavailableOnlineInactive,
        accentColors: ['#3D80FB'],
    },
    'Police, Unmanned, Online, Inactive': {
        svg: policeUnmannedOnlineInactive,
        accentColors: ['#305CE0'],
    },
    'Police, On Foot, Online, Inactive': {
        svg: policeOnFootOnlineInactive,
        accentColors: ['#3D80FB'],
    },
    'Police, On Foot Available, Online, Inactive': {
        svg: policeOnFootOnlineAvailableInactive,
        accentColors: ['#8EC5FF'],
    },
    // The dark red inner disc stays; only the status ring changes.
    'Fire, Available, Online, Inactive': {
        svg: fireAvailableOnlineInactive,
        accentColors: ['#ED8780'],
    },
    'Fire, Unavailable, Online, Inactive': {
        svg: fireUnavailableOnlineInactive,
        accentColors: ['#C22E31'],
    },
    'Fire, Unmanned, Online, Inactive': {
        svg: fireUnmannedOnlineInactive,
        accentColors: ['#A8161E'],
    },
    'EMS, Available, Online, Inactive': {
        svg: emsAvailableOnlineInactive,
        accentColors: ['#9FE6B7'],
    },
    'EMS, Unavailable, Online, Inactive': {
        svg: emsUnavailableOnlineInactive,
        accentColors: ['#20975A'],
    },
};

function staleIcon(onlineIcon: TintableIcon, offlineSvg: string): TintableIcon {
    return { ...onlineIcon, svg: toStaleSvg(onlineIcon.svg, offlineSvg) };
}

// A stale-GPS unit with a configured color keeps that color at 85% opacity. Without one it keeps
// today's gray offline icon.
const tintableUnitIcons = createTintedIconRegistry({
    ...onlineUnitIcons,
    'Police, Available, Offline, Inactive': staleIcon(
        onlineUnitIcons['Police, Available, Online, Inactive'],
        policeAvailableOfflineInactive
    ),
    'Police, Unavailable, Offline, Inactive': staleIcon(
        onlineUnitIcons['Police, Unavailable, Online, Inactive'],
        policeUnavailableOfflineInactive
    ),
    'Police, Unmanned, Offline, Inactive': staleIcon(
        onlineUnitIcons['Police, Unmanned, Online, Inactive'],
        policeUnmannedOfflineInactive
    ),
    'Police, On Foot, Offline, Inactive': staleIcon(
        onlineUnitIcons['Police, On Foot, Online, Inactive'],
        policeOnFootOfflineInactive
    ),
    'Fire, Available, Offline, Inactive': staleIcon(
        onlineUnitIcons['Fire, Available, Online, Inactive'],
        fireAvailableOfflineInactive
    ),
    'Fire, Unavailable, Offline, Inactive': staleIcon(
        onlineUnitIcons['Fire, Unavailable, Online, Inactive'],
        fireUnavailableOfflineInactive
    ),
    'Fire, Unmanned, Offline, Inactive': staleIcon(
        onlineUnitIcons['Fire, Unmanned, Online, Inactive'],
        fireUnmannedOfflineInactive
    ),
    'EMS, Available, Offline, Inactive': staleIcon(
        onlineUnitIcons['EMS, Available, Online, Inactive'],
        emsAvailableOfflineInactive
    ),
    'EMS, Unavailable, Offline, Inactive': staleIcon(
        onlineUnitIcons['EMS, Unavailable, Online, Inactive'],
        emsUnavailableOfflineInactive
    ),
});

// Today's theme is only active while the config is empty, so nothing tints these yet.
const todayTintableEventIcons = createTintedIconRegistry({
    'Assigned, Inactive': { svg: todayEventAssignedInactiveSvg, accentColors: ['#d04341'] },
    'Unassigned, Inactive': { svg: todayEventUnassignedInactiveSvg, accentColors: ['#d04341'] },
});

const redesignedTintableEventIcons = createTintedIconRegistry({
    'Assigned, Inactive': { svg: redesignedEventAssignedInactiveSvg, accentColors: ['#d04341'] },
    'Unassigned, Inactive': {
        svg: redesignedEventUnassignedInactiveSvg,
        accentColors: ['#d04341'],
    },
});

function toUrlByIconName(infos: UniqueValueInfo[]): Record<string, string> {
    return Object.fromEntries(infos.map((info) => [info.value, info.symbol.url]));
}

/** Returns `renderer` itself when nothing is configured, so an unconfigured map renders as before. */
function withTintedIcons<T extends UniqueValueInfo, R extends { uniqueValueInfos: T[] }>(
    renderer: R,
    tintableIcons: TintedIconRegistry
): R {
    const uniqueValueInfos = tintableIcons.withTintedUniqueValueInfos(
        renderer.uniqueValueInfos,
        collectConfiguredHexes(colorSoundConfigStore.getConfiguredColors())
    );
    return uniqueValueInfos === renderer.uniqueValueInfos
        ? renderer
        : { ...renderer, uniqueValueInfos };
}

function createMapIconTheme(assets: MapIconThemeAssets): MapIconTheme {
    const { tintableUnitIcons, tintableEventIcons } = assets;
    const unitIconUrlByName = toUrlByIconName(assets.unitIconRenderer.uniqueValueInfos);
    const eventIconUrlByName = toUrlByIconName(assets.eventIconRenderer.uniqueValueInfos);

    return {
        eventIconSize: assets.eventIconSize,
        unitIconLabels: assets.unitIconLabels,
        eventIconLabels: assets.eventIconLabels,
        raisesSelectedEvent: assets.raisesSelectedEvent,
        eventHoverSymbol: assets.eventHoverSymbol,
        getUnitIconRenderer() {
            return withTintedIcons(assets.unitIconRenderer, tintableUnitIcons);
        },
        getEventIconRenderer() {
            return withTintedIcons(assets.eventIconRenderer, tintableEventIcons);
        },
        getUnitIconName(baseIconName, unitStateId) {
            return tintableUnitIcons.getTintedIconName(
                baseIconName,
                getConfiguredHex(colorSoundConfigStore.resolveUnitStateColor(unitStateId))
            );
        },
        getEventIconName(baseIconName, priorityAttributeId, agencyTypeGlobalAttrId) {
            return tintableEventIcons.getTintedIconName(
                baseIconName,
                getConfiguredHex(
                    colorSoundConfigStore.resolvePriorityColor(
                        priorityAttributeId,
                        agencyTypeGlobalAttrId
                    )
                )
            );
        },
        getUnitIconUrl(iconName) {
            if (!iconName) {
                return '';
            }
            return (
                unitIconUrlByName[iconName] ?? tintableUnitIcons.getTintedIconUrl(iconName) ?? ''
            );
        },
        getEventIconUrl(iconName) {
            return (
                eventIconUrlByName[iconName] ?? tintableEventIcons.getTintedIconUrl(iconName) ?? ''
            );
        },
    };
}

const todayMapIconTheme = createMapIconTheme({
    unitIconRenderer,
    eventIconRenderer,
    eventIconSize: EVENT_ICON_SIZE,
    unitIconLabels,
    eventIconLabels,
    raisesSelectedEvent: false,
    eventHoverSymbol: null,
    tintableUnitIcons,
    tintableEventIcons: todayTintableEventIcons,
});

const redesignedSelectedUnitIconSvgs: Record<string, string> = {
    'Police, Available, Online, Selected': redesignedPoliceOnlineSelectedSvg,
    'Police, Available, Offline, Selected': redesignedPoliceOfflineSelectedSvg,
    'Police, Unavailable, Online, Selected': redesignedPoliceOnlineSelectedSvg,
    'Police, Unavailable, Online, Selected, EN_ROUTE_ALTERNATE': redesignedPoliceOnlineSelectedSvg,
    'Police, Unavailable, Offline, Selected': redesignedPoliceOfflineSelectedSvg,
    'Police, Unavailable, Offline, Selected, EN_ROUTE_ALTERNATE': redesignedPoliceOfflineSelectedSvg,
    'Police, Unmanned, Online, Selected': redesignedPoliceOnlineSelectedSvg,
    'Police, Unmanned, Offline, Selected': redesignedPoliceOfflineSelectedSvg,
    'Police, On Foot, Online, Selected': redesignedPoliceOnFootOnlineSelectedSvg,
    'Police, On Foot Available, Online, Selected': redesignedPoliceOnFootOnlineSelectedSvg,
    'Police, On Foot, Offline, Selected': redesignedPoliceOnFootOfflineSelectedSvg,
    'Fire, Available, Online, Selected': redesignedFireOnlineSelectedSvg,
    'Fire, Available, Offline, Selected': redesignedFireOfflineSelectedSvg,
    'Fire, Unavailable, Online, Selected': redesignedFireOnlineSelectedSvg,
    'Fire, Unavailable, Online, Selected, EN_ROUTE_ALTERNATE': redesignedFireOnlineSelectedSvg,
    'Fire, Unavailable, Offline, Selected': redesignedFireOfflineSelectedSvg,
    'Fire, Unavailable, Offline, Selected, EN_ROUTE_ALTERNATE': redesignedFireOfflineSelectedSvg,
    'Fire, Unmanned, Online, Selected': redesignedFireOnlineSelectedSvg,
    'Fire, Unmanned, Offline, Selected': redesignedFireOfflineSelectedSvg,
    'EMS, Available, Online, Selected': redesignedEmsOnlineSelectedSvg,
    'EMS, Available, Offline, Selected': redesignedEmsOfflineSelectedSvg,
    'EMS, Unavailable, Online, Selected': redesignedEmsOnlineSelectedSvg,
    'EMS, Unavailable, Online, Selected, EN_ROUTE_ALTERNATE': redesignedEmsOnlineSelectedSvg,
    'EMS, Unavailable, Offline, Selected': redesignedEmsOfflineSelectedSvg,
    'EMS, Unavailable, Offline, Selected, EN_ROUTE_ALTERNATE': redesignedEmsOfflineSelectedSvg,
};

export interface SelectionColors {
    fill: string;
    border: string;
    content: string;
}

/**
 * Arc's selection colors for a color mode, as hex: map icons are `data:` URLs and text symbols,
 * which can't read Arc's CSS variables.
 */
export function getSelectionColors(colorMode: Theme): SelectionColors {
    const { selected } = tokens[colorMode].colors;
    return { fill: selected.default, border: selected.emphasis, content: selected.content };
}

// The colors the selected unit and event SVGs are drawn in: Arc's dark mode selection colors. On an
// event the outer diamond is the border and the inner diamond the fill; its priority label, the
// content, is a text symbol. A unit's glyph and its stale pin are drawn in the content color.
const SELECTED_SVG_FILL = '#8EC5FF';
const SELECTED_SVG_BORDER = '#356CEF';
const SELECTED_SVG_CONTENT = '#1E1F20';

function toSelectedIconUrl(svg: string, selectionColors: SelectionColors): string {
    return svgToDataUrl(
        replaceColors(svg, {
            [SELECTED_SVG_FILL]: selectionColors.fill,
            [SELECTED_SVG_BORDER]: selectionColors.border,
            [SELECTED_SVG_CONTENT]: selectionColors.content,
        })
    );
}

// The redesigned SVGs pad the diamond to leave room for its drop shadow. At this size the diamond is
// about as big as today's icon.
const REDESIGNED_EVENT_ICON_SIZE = '42px';

const [inactiveUnitIconLabel, selectedUnitIconLabel] = unitIconLabels;
const [eventIconLabel] = eventIconLabels;

// Esri pads a label's background box about 13px above its text, so today's 7pt lift puts the box
// over the lower third of the disc. `yoffset` is in points: at -2 the box starts about 4px below a
// 32px unit icon's disc.
const REDESIGNED_SELECTED_UNIT_LABEL_YOFFSET = -2;

const redesignedSelectedUnitIconLabel: LabelClassProperties =
    selectedUnitIconLabel.symbol?.type === 'text'
        ? {
              ...selectedUnitIconLabel,
              symbol: {
                  ...selectedUnitIconLabel.symbol,
                  color: colors.mapLabelText,
                  backgroundColor: colors.mapSelectedLabelBackground,
                  yoffset: REDESIGNED_SELECTED_UNIT_LABEL_YOFFSET,
              },
          }
        : selectedUnitIconLabel;

function createRedesignedMapIconTheme(colorMode: Theme): MapIconTheme {
    const selectionColors = getSelectionColors(colorMode);
    const eventIconUrls: Record<string, string> = {
        'Assigned, Inactive': redesignedEventAssignedInactive,
        'Assigned, Selected': toSelectedIconUrl(
            redesignedEventAssignedSelectedSvg,
            selectionColors
        ),
        'Unassigned, Inactive': redesignedEventUnassignedInactive,
        'Unassigned, Selected': toSelectedIconUrl(
            redesignedEventUnassignedSelectedSvg,
            selectionColors
        ),
    };

    return createMapIconTheme({
        unitIconRenderer: {
            ...unitIconRenderer,
            uniqueValueInfos: unitUniqueValueInfos.map(function toRedesignedUnitUniqueValueInfo(
                info
            ) {
                const svg = redesignedSelectedUnitIconSvgs[info.value];
                return svg
                    ? {
                          ...info,
                          symbol: { ...info.symbol, url: toSelectedIconUrl(svg, selectionColors) },
                      }
                    : info;
            }),
        },
        eventIconRenderer: {
            ...eventIconRenderer,
            uniqueValueInfos: eventUniqueValueInfos.map(function toRedesignedEventUniqueValueInfo(
                info
            ) {
                return {
                    ...info,
                    symbol: {
                        ...info.symbol,
                        url: eventIconUrls[info.value] ?? info.symbol.url,
                        width: REDESIGNED_EVENT_ICON_SIZE,
                        height: REDESIGNED_EVENT_ICON_SIZE,
                    },
                };
            }),
        },
        eventIconSize: REDESIGNED_EVENT_ICON_SIZE,
        unitIconLabels: [inactiveUnitIconLabel, redesignedSelectedUnitIconLabel],
        eventIconLabels: [
            { ...eventIconLabel, where: `uiState<>'Selected'` },
            {
                ...withTextLabelColor(eventIconLabel, selectionColors.content),
                where: `uiState='Selected'`,
            },
        ],
        raisesSelectedEvent: true,
        eventHoverSymbol: {
            type: 'picture-marker',
            url: svgToDataUrl(EVENT_HOVER_OUTLINE_SVG),
            width: REDESIGNED_EVENT_ICON_SIZE,
            height: REDESIGNED_EVENT_ICON_SIZE,
        },
        tintableUnitIcons,
        tintableEventIcons: redesignedTintableEventIcons,
    });
}

const redesignedMapIconThemes: Record<Theme, MapIconTheme> = {
    light: createRedesignedMapIconTheme('light'),
    dark: createRedesignedMapIconTheme('dark'),
};

/**
 * The only place the map decides between today's icons and the redesign. The color and sound
 * config is set at bootstrap, before any map renders, and only changes on reload, so callers ask
 * when they build a layer or an icon instead of reacting to it. A config this client can't read
 * leaves the store empty too, so it gets today's icons rather than the redesign without colors.
 *
 * In the redesign, selected unit and event icons and a selected event's label follow `colorMode`;
 * nothing else does. Map layers pass the basemap's mode, from `getMapIconColorMode`. Today's icons
 * look the same in both modes, so this returns the same theme for either.
 */
export function getMapIconTheme(colorMode: Theme = 'dark'): MapIconTheme {
    return colorSoundConfigStore.isEnabled ? redesignedMapIconThemes[colorMode] : todayMapIconTheme;
}

/**
 * The text color of the selected unit's row in the units cluster popup. Esri's popup is always
 * dark, whatever the basemap, so the redesign uses the dark mode selection color.
 */
export function getClusterPopupSelectedLabelColor(): string {
    return colorSoundConfigStore.isEnabled
        ? getSelectionColors('dark').fill
        : cssVar('arc.colors.raw.orange.400');
}

// TODO: RND-20565 - Type this with the cad-api views once MapAssignedUnitInfo exposes unitStateId;
// the viewport and ledger unit views already do.
type WithUnitStateId = { unitStateId?: number | null };
// The viewport and ledger event views name the priority attribute id differently.
type WithPriorityAttributeId = Partial<
    Pick<MapViewportEventView, 'priorityAttributeId'> & Pick<MapLedgerEventView, 'priorityAttrId'>
>;

export function getUnitStateId(view: object | null | undefined): number | null {
    return (view as WithUnitStateId | null | undefined)?.unitStateId ?? null;
}

export function getEventPriorityAttributeId(
    view: WithPriorityAttributeId | null | undefined
): number | null {
    return view?.priorityAttributeId ?? view?.priorityAttrId ?? null;
}
